import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Plus, Pencil, Trash2, Upload, X, Save, Package, ExternalLink } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "efs_admin_token";
const authHeader = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}` } });
const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (iso) => { try { return new Date(iso).toLocaleString(); } catch { return iso; } };

const CATS = ["Auto Scrubbers", "Paper Products", "Chemicals", "Dispensers", "Accessories"];
const emptyP = { name: "", category: "Accessories", description: "", image: "", supplier_name: "", supplier_url: "", supplier_sku: "", cost: 0, price: 0, shipping_fee: 0, active: true };

export function SupplyProductsPanel({ onAuthError }) {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyP);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [markupPct, setMarkupPct] = useState(35);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await axios.get(`${API}/supply/products/all`, authHeader()); setProducts(data); }
    catch (err) { if (err.response?.status === 401) onAuthError(); else toast.error("Could not load products."); }
    finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditing("new"); setForm(emptyP); };
  const openEdit = (p) => { setEditing(p.id); setForm({ ...emptyP, ...p }); };
  const close = () => { setEditing(null); setForm(emptyP); };
  const up = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const uploadImage = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await axios.post(`${API}/uniform/products/image`, fd, { headers: { ...authHeader().headers, "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, image: `${API}/uniform/products/image/${data.image_path}` }));
      toast.success("Image uploaded.");
    } catch (err) { if (err.response?.status === 401) onAuthError(); else toast.error("Upload failed."); }
    finally { setUploading(false); }
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Product name is required."); return; }
    if (!form.image.trim()) { toast.error("Please add a product image."); return; }
    setSaving(true);
    const payload = {
      name: form.name.trim(), category: form.category, description: (form.description || "").trim(), image: form.image.trim(),
      supplier_name: (form.supplier_name || "").trim(), supplier_url: (form.supplier_url || "").trim(), supplier_sku: (form.supplier_sku || "").trim(),
      cost: parseFloat(form.cost) || 0, price: parseFloat(form.price) || 0, shipping_fee: parseFloat(form.shipping_fee) || 0, active: !!form.active,
    };
    try {
      if (editing === "new") await axios.post(`${API}/supply/products`, payload, authHeader());
      else await axios.put(`${API}/supply/products/${editing}`, payload, authHeader());
      toast.success("Product saved."); close(); load();
    } catch (err) { if (err.response?.status === 401) onAuthError(); else toast.error(err.response?.data?.detail || "Could not save."); }
    finally { setSaving(false); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Remove "${p.name}" from the store?`)) return;
    try { await axios.delete(`${API}/supply/products/${p.id}`, authHeader()); toast.success("Removed."); load(); }
    catch (err) { if (err.response?.status === 401) onAuthError(); else toast.error("Could not remove."); }
  };

  const inp = "w-full border border-slate-300 rounded-sm px-3 py-2.5 text-sm focus:border-[#1c4f9c] focus:ring-1 focus:ring-[#1c4f9c] outline-none";
  const margin = (parseFloat(form.price) || 0) - (parseFloat(form.cost) || 0);
  const applyMarkup = () => {
    const cost = parseFloat(form.cost) || 0;
    const pct = parseFloat(markupPct) || 0;
    if (cost <= 0) { toast.error("Enter your cost first."); return; }
    setForm((f) => ({ ...f, price: Math.round(cost * (1 + pct / 100) * 100) / 100 }));
  };

  return (
    <div data-testid="supply-products-panel">
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-slate-500">Set supplier, your cost and the retail price. Profit is calculated for you.</p>
        <button onClick={openNew} data-testid="add-supply-product-btn" className="inline-flex items-center gap-2 text-sm font-bold text-white bg-[#1c4f9c] rounded-sm px-4 py-2 hover:bg-[#163f7d] transition-colors"><Plus size={16} /> Add Product</button>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="animate-spin text-[#1c4f9c]" size={28} /></div>
      ) : products.length === 0 ? (
        <p className="p-10 text-center text-slate-400 bg-white border border-slate-200 rounded-sm">No products yet.</p>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {products.map((p) => (
            <div key={p.id} data-testid={`supply-card-${p.id}`} className={`bg-white border rounded-sm overflow-hidden ${p.active !== false ? "border-slate-200" : "border-slate-200 opacity-60"}`}>
              <div className="aspect-square bg-slate-100 overflow-hidden"><img src={p.image} alt={p.name} className="w-full h-full object-cover" /></div>
              <div className="p-4">
                <span className="text-[11px] font-bold uppercase text-[#e02329]">{p.category}</span>
                <h3 className="font-bold text-[#1c4f9c] leading-tight">{p.name}</h3>
                <p className="mt-1 text-xs text-slate-400">Supplier: {p.supplier_name || "—"}{p.supplier_sku ? ` · ${p.supplier_sku}` : ""}</p>
                <div className="mt-2 grid grid-cols-3 gap-1 text-center text-xs">
                  <div className="bg-slate-50 rounded-sm py-1"><div className="text-slate-400">Cost</div><div className="font-bold text-slate-700">{money(p.cost)}</div></div>
                  <div className="bg-slate-50 rounded-sm py-1"><div className="text-slate-400">Price</div><div className="font-bold text-slate-700">{money(p.price)}</div></div>
                  <div className="bg-green-50 rounded-sm py-1"><div className="text-green-600">Profit</div><div className="font-bold text-green-700">{money(p.price - p.cost)}</div></div>
                </div>
                {p.active === false && <span className="mt-2 inline-block text-[10px] font-bold uppercase bg-slate-200 text-slate-500 px-2 py-0.5 rounded-sm">Inactive</span>}
                <div className="mt-3 flex gap-2">
                  <button onClick={() => openEdit(p)} data-testid={`edit-supply-${p.id}`} className="flex-1 inline-flex items-center justify-center gap-1 text-xs font-bold text-[#1c4f9c] border border-slate-200 rounded-sm py-2 hover:border-[#1c4f9c] transition-colors"><Pencil size={13} /> Edit</button>
                  <button onClick={() => remove(p)} data-testid={`delete-supply-${p.id}`} className="inline-flex items-center justify-center text-xs font-bold text-[#e02329] border border-slate-200 rounded-sm px-3 py-2 hover:border-[#e02329] transition-colors"><Trash2 size={13} /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" data-testid="supply-product-modal">
          <div className="bg-white rounded-sm w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-slate-200 sticky top-0 bg-white">
              <h3 className="font-bold text-[#1c4f9c] text-lg">{editing === "new" ? "Add Product" : "Edit Product"}</h3>
              <button onClick={close} className="text-slate-400 hover:text-slate-700"><X size={22} /></button>
            </div>
            <div className="p-5 space-y-4">
              <div><label className="block text-xs font-semibold text-slate-600 mb-1.5">Product name *</label><input data-testid="sp-name" value={form.name} onChange={up("name")} className={inp} /></div>
              <div><label className="block text-xs font-semibold text-slate-600 mb-1.5">Category</label>
                <select data-testid="sp-category" value={form.category} onChange={up("category")} className={inp}>{CATS.map((c) => <option key={c} value={c}>{c}</option>)}</select>
              </div>
              <div><label className="block text-xs font-semibold text-slate-600 mb-1.5">Description</label><textarea data-testid="sp-description" value={form.description} onChange={up("description")} rows={2} className={inp} /></div>
              <div className="grid grid-cols-3 gap-3">
                <div><label className="block text-xs font-semibold text-slate-600 mb-1.5">Your cost ($)</label><input data-testid="sp-cost" type="number" step="0.01" value={form.cost} onChange={up("cost")} className={inp} /></div>
                <div><label className="block text-xs font-semibold text-slate-600 mb-1.5">Retail price ($)</label><input data-testid="sp-price" type="number" step="0.01" value={form.price} onChange={up("price")} className={inp} /></div>
                <div><label className="block text-xs font-semibold text-slate-600 mb-1.5">Shipping ($)</label><input data-testid="sp-shipping" type="number" step="0.01" value={form.shipping_fee} onChange={up("shipping_fee")} className={inp} /></div>
              </div>
              <div className={`text-sm font-semibold rounded-sm px-3 py-2 ${margin >= 0 ? "bg-green-50 text-green-700" : "bg-red-50 text-[#e02329]"}`}>Profit per unit: {money(margin)}</div>
              <div className="flex items-end gap-2 bg-slate-50 rounded-sm p-3">
                <div className="flex-1">
                  <label className="block text-xs font-semibold text-slate-600 mb-1.5">Quick markup (%)</label>
                  <input data-testid="sp-markup" type="number" step="1" value={markupPct} onChange={(e) => setMarkupPct(e.target.value)} className={inp} placeholder="35" />
                </div>
                <button type="button" onClick={applyMarkup} data-testid="sp-apply-markup" className="inline-flex items-center gap-1 text-sm font-bold text-white bg-[#1c4f9c] rounded-sm px-4 py-2.5 hover:bg-[#163f7d] transition-colors whitespace-nowrap">Apply to price</button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div><label className="block text-xs font-semibold text-slate-600 mb-1.5">Supplier name</label><input data-testid="sp-supplier" value={form.supplier_name} onChange={up("supplier_name")} className={inp} placeholder="e.g. Uline" /></div>
                <div><label className="block text-xs font-semibold text-slate-600 mb-1.5">Supplier SKU</label><input data-testid="sp-sku" value={form.supplier_sku} onChange={up("supplier_sku")} className={inp} /></div>
              </div>
              <div><label className="block text-xs font-semibold text-slate-600 mb-1.5">Supplier URL (where you order)</label><input data-testid="sp-supplier-url" value={form.supplier_url} onChange={up("supplier_url")} className={inp} placeholder="https://..." /></div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1.5">Product image *</label>
                <div className="flex items-center gap-3">
                  {form.image ? <img src={form.image} alt="" className="w-16 h-16 object-cover bg-slate-100 rounded-sm border border-slate-200" /> : <div className="w-16 h-16 bg-slate-100 rounded-sm border border-slate-200 flex items-center justify-center text-slate-300"><Package size={22} /></div>}
                  <label className="inline-flex items-center gap-2 text-sm font-semibold text-[#1c4f9c] border border-slate-300 rounded-sm px-3 py-2 cursor-pointer hover:border-[#1c4f9c] transition-colors">
                    {uploading ? <Loader2 className="animate-spin" size={15} /> : <Upload size={15} />} Upload
                    <input data-testid="sp-image-upload" type="file" accept="image/png,image/jpeg,image/webp" onChange={uploadImage} className="hidden" />
                  </label>
                </div>
                <input data-testid="sp-image-url" value={form.image} onChange={up("image")} className={`${inp} mt-2`} placeholder="…or paste an image URL" />
              </div>
              <label className="flex items-center gap-2 text-sm font-semibold text-slate-600"><input data-testid="sp-active" type="checkbox" checked={form.active} onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))} className="w-4 h-4" /> Show in store (active)</label>
            </div>
            <div className="p-5 border-t border-slate-200 flex justify-end gap-2 sticky bottom-0 bg-white">
              <button onClick={close} className="text-sm font-bold text-slate-500 px-4 py-2.5 hover:text-slate-700">Cancel</button>
              <button onClick={save} disabled={saving} data-testid="save-supply-btn" className="inline-flex items-center gap-2 text-sm font-bold text-white bg-[#e02329] rounded-sm px-5 py-2.5 hover:bg-[#c81e24] transition-colors disabled:opacity-70">{saving ? <Loader2 className="animate-spin" size={16} /> : <Save size={16} />} Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export function SupplyOrdersPanel({ orders }) {
  if (!orders.length) return <p className="p-10 text-center text-slate-400 bg-white border border-slate-200 rounded-sm">No supply orders yet.</p>;
  return (
    <div data-testid="supply-orders-panel" className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
          <tr>
            <th className="px-4 py-3">Order / Customer</th><th className="px-4 py-3">Items (order from supplier)</th>
            <th className="px-4 py-3">Charged</th><th className="px-4 py-3">Your cost</th><th className="px-4 py-3">Profit</th>
            <th className="px-4 py-3">Status</th><th className="px-4 py-3">Date</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {orders.map((o) => (
            <tr key={o.id} className="align-top hover:bg-slate-50">
              <td className="px-4 py-3">
                {o.customer ? (
                  <div className="text-xs text-slate-600">
                    <div className="font-semibold text-[#1c4f9c]">{o.customer.name}</div>
                    {o.customer.line1}{o.customer.line2 ? `, ${o.customer.line2}` : ""}<br />
                    {o.customer.city}, {o.customer.state} {o.customer.postal_code}
                    {o.customer.phone ? <><br />{o.customer.phone}</> : null}
                    {o.customer_email ? <><br />{o.customer_email}</> : null}
                  </div>
                ) : <span className="text-xs text-slate-400">Awaiting payment</span>}
              </td>
              <td className="px-4 py-3 text-slate-600">
                {(o.items || []).map((it, k) => (
                  <div key={k} className="mb-1">
                    {it.qty}× {it.name}
                    <span className="block text-xs text-slate-400">
                      supplier: {it.supplier_name || "—"}{it.supplier_sku ? ` · ${it.supplier_sku}` : ""} · cost {money(it.unit_cost)}
                      {it.supplier_url ? <a href={it.supplier_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-0.5 text-[#1c4f9c] ml-1 hover:underline">order <ExternalLink size={10} /></a> : null}
                    </span>
                  </div>
                ))}
              </td>
              <td className="px-4 py-3 font-semibold text-slate-700">{money(o.amount)}<div className="text-xs font-normal text-slate-400">+{money(o.shipping_total)} ship</div></td>
              <td className="px-4 py-3 text-slate-600">{money(o.cost_total)}</td>
              <td className="px-4 py-3 font-bold text-green-700">{money(o.profit)}</td>
              <td className="px-4 py-3"><span className={`text-xs font-bold uppercase px-2 py-1 rounded-sm ${o.payment_status === "paid" ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}`}>{o.payment_status === "paid" ? "paid" : o.status}</span></td>
              <td className="px-4 py-3 text-slate-400 whitespace-nowrap">{fmtDate(o.created_at)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function SupplyQuotesPanel({ quotes }) {
  if (!quotes.length) return <p className="p-10 text-center text-slate-400 bg-white border border-slate-200 rounded-sm">No quote requests yet.</p>;
  return (
    <div data-testid="supply-quotes-panel" className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
          <tr><th className="px-4 py-3">Name / Company</th><th className="px-4 py-3">Contact</th><th className="px-4 py-3">Message</th><th className="px-4 py-3">Date</th></tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {quotes.map((q) => (
            <tr key={q.id} className="align-top hover:bg-slate-50">
              <td className="px-4 py-3"><div className="font-semibold text-[#1c4f9c]">{q.name}</div><div className="text-xs text-slate-400">{q.company || "—"}</div></td>
              <td className="px-4 py-3 text-slate-600 text-xs">{q.email}<br />{q.phone}</td>
              <td className="px-4 py-3 text-slate-600 max-w-md">{q.message}</td>
              <td className="px-4 py-3 text-slate-400 whitespace-nowrap">{fmtDate(q.created_at)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
